// =============================================================================
// projectFinancialsExport.ts
//
// Project cost report: what the budget was (from the accepted quote) vs what
// the actuals are (AP spend + costed labour/LAFHA), with every spend line
// tracked to its budget code. Two outputs:
//   • exportProjectFinancialsXlsx — a workbook with tabs (Summary, Budget vs
//     Spend, Planned (from quote), Spend detail, Client invoices).
//   • exportProjectFinancialsPdf — a one-page branded summary report.
//
// Data mirrors the Financials tab exactly (budget_lines = planned; budget_spend
// = committed/actual, where labour/LAFHA are costed in via the timesheet
// trigger and AP invoices via the AP sync trigger). All figures ex-GST.
// =============================================================================

import * as XLSX from "xlsx";
import { supabase } from "@/lib/supabase";

// @ts-ignore — Vite ?url suffix isn't in pdfmake's type declarations
import pdfmakeScriptUrl from "pdfmake/build/pdfmake.min.js?url";
// @ts-ignore
import vfsFontsScriptUrl from "pdfmake/build/vfs_fonts.js?url";

const num = (v: any) => Number(v ?? 0);
const money = (v: number) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const safe = (s: string) => (s || "project").replace(/[^\w.-]+/g, "_").slice(0, 60);

interface Financials {
  project: any;
  quote: any | null;
  budgetLines: any[];
  spend: any[];
  codeLabel: Map<string, string>;
  invoices: any[];
  codeRows: { code: string; label: string; planned: number; committed: number; actual: number; remaining: number }[];
  totals: { planned: number; quotedSell: number; committed: number; actual: number; contract: number; invoiced: number; paid: number; forecastMargin: number; forecastMarginPct: number };
}

async function loadFinancials(projectId: string): Promise<Financials> {
  const { data: project } = await supabase
    .from("projects")
    .select("project_id, project_code, project_name, status, contract_value, invoiced_to_date, paid_to_date, source_quote_id, clients ( legal_name )")
    .eq("project_id", projectId)
    .maybeSingle();

  const [quoteRes, blRes, spRes, invRes, fbcRes] = await Promise.all([
    (project as any)?.source_quote_id
      ? supabase.from("quotes").select("quote_number, subtotal_excl_gst, total_incl_gst, margin_percent, accepted_at").eq("quote_id", (project as any).source_quote_id).maybeSingle()
      : Promise.resolve({ data: null }),
    supabase.from("budget_lines")
      .select("budget_code, category, description, planned_qty, planned_unit, planned_amount_ex_gst, quoted_sell_ex_gst, sort_order")
      .eq("project_id", projectId).eq("is_active", true).order("sort_order"),
    supabase.from("budget_spend")
      .select("budget_code, spend_type, amount_ex_gst, spend_date, reference_no, description, status, supplier_name_cache, source_record_type, source_record_id")
      .eq("project_id", projectId).order("spend_date", { ascending: false }),
    supabase.from("client_invoices")
      .select("invoice_number, total_incl_gst, status, invoice_date, paid_at")
      .eq("project_id", projectId).order("invoice_date", { ascending: false }),
    supabase.from("finance_budget_codes").select("budget_code, description, label"),
  ]);

  const budgetLines = (blRes.data ?? []) as any[];
  const spend = (spRes.data ?? []) as any[];
  const invoices = (invRes.data ?? []) as any[];

  // Enrich AP-invoice spend rows with the supplier + invoice number (the sync
  // trigger doesn't cache these onto budget_spend), so the detail shows which
  // invoice each line is. Labour/TA rows already carry their own description.
  const apIds = [...new Set(spend.filter(s => s.source_record_type === "ap_invoice" && s.source_record_id).map(s => String(s.source_record_id)))];
  const apMap = new Map<string, any>();
  if (apIds.length) {
    const { data: aps } = await supabase.from("ap_invoices").select("ap_invoice_id, supplier_name, invoice_number").in("ap_invoice_id", apIds);
    for (const a of (aps ?? []) as any[]) apMap.set(String(a.ap_invoice_id), a);
  }
  for (const s of spend) {
    if (s.source_record_type === "ap_invoice") {
      const a = apMap.get(String(s.source_record_id));
      s._supplier = a?.supplier_name || s.supplier_name_cache || "AP invoice";
      s._ref = a?.invoice_number || s.reference_no || "";
    } else {
      s._supplier = s.supplier_name_cache || s.description || "";
      s._ref = s.reference_no || "";
    }
  }

  // T-380 — resolve PO-commitment rows to their PO number for the detail.
  const poIds = [...new Set(spend.filter(s => s.source_record_type === "purchase_order" && s.source_record_id).map(s => String(s.source_record_id)))];
  if (poIds.length) {
    const { data: pos } = await supabase.from("purchase_orders").select("po_id, po_number, supplier_id").in("po_id", poIds);
    const poMap = new Map((pos ?? []).map((p: any) => [String(p.po_id), p]));
    for (const s of spend) {
      if (s.source_record_type !== "purchase_order") continue;
      const p = poMap.get(String(s.source_record_id));
      s._ref = p?.po_number ?? s._ref ?? "";
      if (!s._supplier) s._supplier = s.description ?? "PO commitment";
    }
  }

  // T-378 — enrich timesheet-sourced rows with WHO + HOURS + NIGHTS so the
  // spend detail is filterable (source_record_id = the timesheet entry_id).
  // NOTE: employees embed must be disambiguated (employees:employee_id) — the
  // mirrored_from_employee_id FK makes a bare employees(...) ambiguous.
  const tsIds = [...new Set(spend.filter(s => s.source_record_type === "timesheet_entry" && s.source_record_id).map(s => String(s.source_record_id)))];
  const tsMap = new Map<string, any>();
  if (tsIds.length) {
    // .in() with many ids is fine at typical project sizes; chunk to be safe.
    for (let i = 0; i < tsIds.length; i += 200) {
      const { data: ts } = await supabase
        .from("timesheet_entries")
        .select("entry_id, total_hours, lafha_nights, employees:employee_id ( first_name, last_name )")
        .in("entry_id", tsIds.slice(i, i + 200));
      for (const t of (ts ?? []) as any[]) tsMap.set(String(t.entry_id), t);
    }
  }
  for (const s of spend) {
    if (s.source_record_type !== "timesheet_entry") continue;
    const t = tsMap.get(String(s.source_record_id));
    if (!t) continue;
    s._employee = [t.employees?.first_name, t.employees?.last_name].filter(Boolean).join(" ") || "";
    const isTravel = s.budget_code === "PX04_TRAVEL" || /travel allowance/i.test(s.description ?? "");
    if (isTravel) {
      s._nights = num(t.lafha_nights) || null;
      s._rate = s._nights ? Math.round((num(s.amount_ex_gst) / s._nights) * 100) / 100 : null;
    } else {
      s._hours = num(t.total_hours) || null;
      s._rate = s._hours ? Math.round((num(s.amount_ex_gst) / s._hours) * 100) / 100 : null;
    }
  }
  const codeLabel = new Map<string, string>();
  for (const c of (fbcRes.data ?? []) as any[]) codeLabel.set(c.budget_code, c.label || c.description || c.budget_code);

  // Per-code rollup — union of planned codes + any code that has spend.
  const codeSet = new Set<string>();
  budgetLines.forEach(b => b.budget_code && codeSet.add(b.budget_code));
  spend.forEach(s => s.budget_code && codeSet.add(s.budget_code));
  const codeRows = [...codeSet].map(code => {
    const planned = budgetLines.filter(b => b.budget_code === code).reduce((a, b) => a + num(b.planned_amount_ex_gst), 0);
    const committed = spend.filter(s => s.budget_code === code && s.spend_type === "committed").reduce((a, s) => a + num(s.amount_ex_gst), 0);
    const actual = spend.filter(s => s.budget_code === code && s.spend_type !== "committed").reduce((a, s) => a + num(s.amount_ex_gst), 0);
    return { code, label: codeLabel.get(code) ?? "", planned, committed, actual, remaining: planned - (committed + actual) };
  }).sort((a, b) => a.code.localeCompare(b.code));

  const planned = budgetLines.reduce((a, b) => a + num(b.planned_amount_ex_gst), 0);
  const quotedSell = budgetLines.reduce((a, b) => a + num(b.quoted_sell_ex_gst), 0);
  const committed = spend.filter(s => s.spend_type === "committed").reduce((a, s) => a + num(s.amount_ex_gst), 0);
  const actual = spend.filter(s => s.spend_type !== "committed").reduce((a, s) => a + num(s.amount_ex_gst), 0);
  const contract = num((project as any)?.contract_value);
  const invoiced = num((project as any)?.invoiced_to_date);
  const paid = num((project as any)?.paid_to_date);
  const forecastMargin = contract - (committed + actual);
  const forecastMarginPct = contract > 0 ? (forecastMargin / contract) * 100 : 0;

  return {
    project, quote: (quoteRes as any).data ?? null, budgetLines, spend, codeLabel, invoices, codeRows,
    totals: { planned, quotedSell, committed, actual, contract, invoiced, paid, forecastMargin, forecastMarginPct },
  };
}

function sourceLabel(t: string | null): string {
  if (t === "ap_invoice") return "AP invoice";
  if (t === "timesheet_entry") return "Labour / TA (timesheet)";
  if (t === "purchase_order") return "PO commitment";   // T-380
  return t || "manual";
}

// ------------------------------------------------------------------ XLSX ---
export async function exportProjectFinancialsXlsx(projectId: string) {
  const f = await loadFinancials(projectId);
  const p = f.project ?? {};
  const code = p.project_code ?? "project";
  const wb = XLSX.utils.book_new();

  // 1. Summary
  const summary: any[][] = [
    ["Project", `${p.project_code ?? ""} — ${p.project_name ?? ""}`],
    ["Client", p.clients?.legal_name ?? ""],
    ["Status", p.status ?? ""],
    ["Report generated", new Date().toLocaleString()],
    ["", ""],
    ["Source quote", f.quote?.quote_number ?? "—"],
    ["Quote accepted", f.quote?.accepted_at ? new Date(f.quote.accepted_at).toLocaleDateString() : "—"],
    ["", ""],
    ["ALL FIGURES EX-GST", ""],
    ["Contract value (client)", f.totals.contract],
    ["Invoiced to date", f.totals.invoiced],
    ["Paid to date", f.totals.paid],
    ["Outstanding", f.totals.contract - f.totals.paid],
    ["", ""],
    ["Planned cost (from quote)", f.totals.planned],
    ["Committed", f.totals.committed],
    ["Actual spend", f.totals.actual],
    ["Committed + actual", f.totals.committed + f.totals.actual],
    ["Forecast margin", f.totals.forecastMargin],
    ["Forecast margin %", `${f.totals.forecastMarginPct.toFixed(1)}%`],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), "Summary");

  // 2. Budget vs Spend (per code)
  const bvs = [
    ["Code", "Description", "Planned (cost)", "Committed", "Actual", "Committed+Actual", "Remaining", "% spent"],
    ...f.codeRows.map(r => [r.code, r.label, r.planned, r.committed, r.actual, r.committed + r.actual, r.remaining,
      r.planned > 0 ? `${(((r.committed + r.actual) / r.planned) * 100).toFixed(0)}%` : "—"]),
    ["TOTAL", "", f.totals.planned, f.totals.committed, f.totals.actual, f.totals.committed + f.totals.actual,
      f.totals.planned - (f.totals.committed + f.totals.actual), ""],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(bvs), "Budget vs Spend");

  // 3. Planned (from quote)
  const planned = [
    ["Code", "Category", "Description", "Qty", "Unit", "Planned cost (ex GST)", "Quoted sell (ex GST)"],
    ...f.budgetLines.map(b => [b.budget_code ?? "", b.category ?? "", b.description ?? "", b.planned_qty ?? "", b.planned_unit ?? "",
      num(b.planned_amount_ex_gst), num(b.quoted_sell_ex_gst)]),
    ["TOTAL", "", "", "", "", f.totals.planned, f.totals.quotedSell],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(planned), "Planned (Quote)");

  // 4. Spend detail (every line tracked to a code)
  // T-378 — Employee / Hours / Nights / Rate as their own columns (populated
  // for timesheet rows, blank for AP), with an autofilter so it sifts easily.
  const detail = [
    ["Date", "Code", "Type", "Source", "Supplier / detail", "Invoice #", "Employee", "Hours", "Nights", "Rate", "Amount (ex GST)", "Status"],
    ...f.spend.map(s => [
      s.spend_date ?? "", s.budget_code ?? "", s.spend_type ?? "", sourceLabel(s.source_record_type),
      s._supplier ?? "", s._ref ?? "",
      s._employee ?? "", s._hours ?? "", s._nights ?? "", s._rate ?? "",
      num(s.amount_ex_gst), s.status ?? "",
    ]),
  ];
  const detailWs = XLSX.utils.aoa_to_sheet(detail);
  detailWs["!autofilter"] = { ref: `A1:L${detail.length}` };
  detailWs["!cols"] = [
    { wch: 11 }, { wch: 22 }, { wch: 10 }, { wch: 20 }, { wch: 38 }, { wch: 14 },
    { wch: 20 }, { wch: 7 }, { wch: 7 }, { wch: 8 }, { wch: 14 }, { wch: 11 },
  ];
  XLSX.utils.book_append_sheet(wb, detailWs, "Spend detail");

  // 4b. Labour by employee (T-378) — hours, nights + $ per person at a glance.
  const byEmp = new Map<string, { hours: number; nights: number; labour: number; ta: number }>();
  for (const s of f.spend) {
    if (!s._employee) continue;
    const e = byEmp.get(s._employee) ?? { hours: 0, nights: 0, labour: 0, ta: 0 };
    if (s._nights) { e.nights += num(s._nights); e.ta += num(s.amount_ex_gst); }
    else { e.hours += num(s._hours); e.labour += num(s.amount_ex_gst); }
    byEmp.set(s._employee, e);
  }
  if (byEmp.size > 0) {
    const empRows = [...byEmp.entries()].sort((a, b) => (b[1].labour + b[1].ta) - (a[1].labour + a[1].ta));
    const empSheet = [
      ["Employee", "Hours", "Nights (TA)", "Labour $ (ex GST)", "TA $ (ex GST)", "Total $ (ex GST)"],
      ...empRows.map(([name, e]) => [name, e.hours, e.nights, e.labour, e.ta, e.labour + e.ta]),
      ["TOTAL",
        empRows.reduce((a, [, e]) => a + e.hours, 0),
        empRows.reduce((a, [, e]) => a + e.nights, 0),
        empRows.reduce((a, [, e]) => a + e.labour, 0),
        empRows.reduce((a, [, e]) => a + e.ta, 0),
        empRows.reduce((a, [, e]) => a + e.labour + e.ta, 0)],
    ];
    const empWs = XLSX.utils.aoa_to_sheet(empSheet);
    empWs["!cols"] = [{ wch: 24 }, { wch: 8 }, { wch: 11 }, { wch: 16 }, { wch: 14 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, empWs, "Labour by employee");
  }

  // 5. Client invoices
  const inv = [
    ["Invoice", "Date", "Status", "Total (inc GST)", "Paid", "Outstanding"],
    ...f.invoices.map(i => [i.invoice_number ?? "", i.invoice_date ?? "", i.status ?? "", num(i.total_incl_gst),
      i.status === "paid" ? num(i.total_incl_gst) : 0, i.status === "paid" ? 0 : num(i.total_incl_gst)]),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(inv), "Client invoices");

  XLSX.writeFile(wb, `${safe(code)}_financials_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

// ------------------------------------------------------------------- PDF ---
function loadScriptOnce(src: string, tag: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-pdfmake-tag="${tag}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") resolve();
      else { existing.addEventListener("load", () => resolve()); existing.addEventListener("error", () => reject(new Error(`reload ${tag}`))); }
      return;
    }
    const s = document.createElement("script");
    s.src = src; s.async = true; s.dataset.pdfmakeTag = tag;
    s.onload = () => { s.dataset.loaded = "true"; resolve(); };
    s.onerror = () => reject(new Error(`load ${tag}`));
    document.head.appendChild(s);
  });
}
async function getPdfMake(): Promise<any> {
  const existing = (window as any).pdfMake;
  if (existing?.vfs && Object.keys(existing.vfs).length > 0) return existing;
  await loadScriptOnce(pdfmakeScriptUrl, "pdfmake-core");
  await loadScriptOnce(vfsFontsScriptUrl, "pdfmake-vfs");
  const pdfMake = (window as any).pdfMake;
  if (!pdfMake) throw new Error("pdfMake missing");
  return pdfMake;
}

const C = { primary: "#ff7844", ink: "#111", text: "#222", muted: "#666", panel: "#f6f6f4", rule: "#e6e6e3", head: "#3a3a3a" };

export async function exportProjectFinancialsPdf(projectId: string) {
  const f = await loadFinancials(projectId);
  const p = f.project ?? {};
  const pdfMake = await getPdfMake();

  const kpi = (label: string, val: string) => ({
    width: "*",
    stack: [{ text: label.toUpperCase(), fontSize: 7, color: C.muted, characterSpacing: 0.5 }, { text: val, fontSize: 13, bold: true, color: C.ink, margin: [0, 2, 0, 0] }],
  });

  const bvsBody = [
    [h("Code"), h("Description"), h("Planned"), h("Committed"), h("Actual"), h("Remaining")],
    ...f.codeRows.map(r => [
      { text: r.code, fontSize: 8 }, { text: r.label, fontSize: 8, color: C.muted },
      rt(money(r.planned)), rt(money(r.committed)), rt(money(r.actual)),
      { ...rt(money(r.remaining)), color: r.remaining < 0 ? "#c0392b" : C.text },
    ]),
    [{ text: "TOTAL", fontSize: 8, bold: true }, "", rt(money(f.totals.planned), true), rt(money(f.totals.committed), true), rt(money(f.totals.actual), true), rt(money(f.totals.planned - (f.totals.committed + f.totals.actual)), true)],
  ];

  const invBody = f.invoices.length ? [
    [h("Invoice"), h("Date"), h("Status"), h("Total inc GST"), h("Outstanding")],
    ...f.invoices.map(i => [{ text: i.invoice_number ?? "", fontSize: 8 }, { text: i.invoice_date ?? "", fontSize: 8 }, { text: i.status ?? "", fontSize: 8 }, rt(money(num(i.total_incl_gst))), rt(money(i.status === "paid" ? 0 : num(i.total_incl_gst)))]),
  ] : null;

  const doc: any = {
    pageSize: "A4", pageMargins: [36, 44, 36, 40],
    defaultStyle: { font: "Roboto", fontSize: 9, color: C.text },
    content: [
      { columns: [
        { stack: [{ text: "CORETEL", fontSize: 14, bold: true, color: C.ink }, { text: "Project financials", fontSize: 8, color: C.primary, characterSpacing: 1 }] },
        { text: new Date().toLocaleDateString(), alignment: "right", fontSize: 8, color: C.muted, margin: [0, 4, 0, 0] },
      ] },
      { text: `${p.project_code ?? ""} — ${p.project_name ?? ""}`, fontSize: 15, bold: true, color: C.ink, margin: [0, 10, 0, 1] },
      { text: [p.clients?.legal_name, p.status].filter(Boolean).join("  ·  "), fontSize: 9, color: C.muted, margin: [0, 0, 0, 2] },
      f.quote?.quote_number ? { text: `From quote ${f.quote.quote_number}${f.quote.accepted_at ? ` · accepted ${new Date(f.quote.accepted_at).toLocaleDateString()}` : ""}`, fontSize: 8, color: C.muted } : { text: "" },

      { margin: [0, 12, 0, 0], table: { widths: ["*", "*", "*", "*"], body: [[
        kpi("Contract (ex GST)", money(f.totals.contract)), kpi("Invoiced", money(f.totals.invoiced)), kpi("Paid", money(f.totals.paid)), kpi("Outstanding", money(f.totals.contract - f.totals.paid)),
      ]] }, layout: panelLayout() },

      { margin: [0, 8, 0, 0], table: { widths: ["*", "*", "*", "*"], body: [[
        kpi("Planned cost", money(f.totals.planned)), kpi("Committed", money(f.totals.committed)), kpi("Actual", money(f.totals.actual)),
        { width: "*", stack: [{ text: "FORECAST MARGIN", fontSize: 7, color: C.muted }, { text: `${money(f.totals.forecastMargin)}  ·  ${f.totals.forecastMarginPct.toFixed(0)}%`, fontSize: 13, bold: true, color: f.totals.forecastMargin < 0 ? "#c0392b" : "#1a8f4c", margin: [0, 2, 0, 0] }] },
      ]] }, layout: panelLayout() },

      { text: "Budget vs spend", fontSize: 10, bold: true, color: C.ink, margin: [0, 16, 0, 6] },
      { table: { headerRows: 1, widths: [70, "*", 62, 62, 62, 62], body: bvsBody }, layout: tableLayout() },

      ...(invBody ? [
        { text: "Client invoices issued", fontSize: 10, bold: true, color: C.ink, margin: [0, 14, 0, 6] },
        { table: { headerRows: 1, widths: ["*", 70, 60, 80, 80], body: invBody }, layout: tableLayout() },
      ] : []),

      { text: "All figures ex-GST. Planned = accepted quote. Actual = AP spend + costed labour/LAFHA from approved timesheets.", fontSize: 7, color: C.muted, italics: true, margin: [0, 14, 0, 0] },
    ],
  };

  pdfMake.createPdf(doc).download(`${safe(p.project_code ?? "project")}_financials_${new Date().toISOString().slice(0, 10)}.pdf`);
}

function h(t: string) { return { text: t, fontSize: 7.5, bold: true, color: "#fff", fillColor: C.head, margin: [0, 2, 0, 2] }; }
function rt(t: string, bold = false) { return { text: t, fontSize: 8, alignment: "right", bold }; }
function panelLayout() {
  return {
    fillColor: () => C.panel,
    hLineColor: () => C.rule, vLineColor: () => C.rule,
    hLineWidth: () => 0.5, vLineWidth: () => 0.5,
    paddingLeft: () => 8, paddingRight: () => 8, paddingTop: () => 7, paddingBottom: () => 7,
  };
}
function tableLayout() {
  return {
    hLineColor: () => C.rule, vLineColor: () => "#fff",
    hLineWidth: () => 0.5, vLineWidth: () => 0,
    fillColor: (i: number) => (i === 0 ? C.head : i % 2 === 0 ? "#f7f7f6" : null),
    paddingLeft: () => 5, paddingRight: () => 5, paddingTop: () => 3, paddingBottom: () => 3,
  };
}
